import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';
import { Button } from '@ooh/ui/components/button';
import { Separator } from '@ooh/ui/components/separator';
import { Badge } from '@ooh/ui/components/badge';
import type { MediaUnitDetail, CostHistoryEntry } from '@ooh/contracts';
import { SupplierSection } from './form-sections/supplier-section';
import { TechnicalSection } from './form-sections/technical-section';
import { LocationSection } from './form-sections/location-section';
import { CostsSection } from './form-sections/costs-section';
import { NotesSection } from './form-sections/notes-section';
import { PhotoUploadSection } from '@ooh/screens';
import { CostHistorySection } from './cost-history-section';
import { TagsSection } from './tags-section';
import { useEditForm } from '../hooks/use-edit-form';
import { useHasWildcardPermission } from '../../../hooks/use-has-wildcard-permission';

function DiscardConfirmBanner({
  onConfirm,
  onCancel,
}: {
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation('inventory');
  return (
    <div
      className="rounded-lg border border-amber-300 bg-amber-50 p-4"
      data-testid="discard-confirm-banner"
    >
      <p className="text-[14px] font-medium text-amber-800 mb-2">{t('form.edit.discardTitle')}</p>
      <p className="text-[12px] text-amber-700 mb-3">{t('form.edit.discardMessage')}</p>
      <div className="flex items-center gap-2">
        <Button
          variant="destructive"
          size="sm"
          data-testid="discard-confirm-btn"
          onClick={onConfirm}
        >
          {t('form.edit.discardConfirm')}
        </Button>
        <Button variant="outline" size="sm" data-testid="discard-cancel-btn" onClick={onCancel}>
          {t('form.edit.discardCancel')}
        </Button>
      </div>
    </div>
  );
}

export function MediaEditForm({
  unit,
  costHistory,
  id,
  catalogSearch,
}: {
  unit: MediaUnitDetail;
  costHistory: CostHistoryEntry[];
  id: string;
  /** Forwarded from the detail page so the round-trip back to it (and onward to the
   * catalog) keeps the catalog's filter/sort state. See `catalog-return-context.ts`. */
  catalogSearch?: string;
}) {
  const { t: tCommon } = useTranslation('common');
  // Derived directly from this unit's own field-visibility flags (the same
  // ones the GET response already used to null cost/supplier values), not a
  // fresh permission-hook call — see costs-section.tsx/supplier-section.tsx
  // prop docs (field-visibility, CU-86cbgv42n).
  const canEditCosts = !unit.costsWithheld;
  const canEditSupplier = !unit.supplierWithheld;
  const hasRentGrant = useHasWildcardPermission('cost.view_rent');
  const canEditRent = canEditCosts && hasRentGrant;
  const {
    t,
    register,
    watch,
    setValue,
    errors,
    handleSubmit,
    onInvalidSubmit,
    updateUnit,
    isPending,
    isFormDirty,
    showDiscardConfirm,
    setShowDiscardConfirm,
    existingPhotos,
    newPhotos,
    fileInputRef,
    photoCountError,
    handleFileChange,
    handleRemoveExistingPhoto,
    handleRemoveNewPhoto,
    supplierOptions,
    formatOptions,
    filteredCounties,
    handleRegionChange,
    area,
    voivodeshipOptions,
    handleCancel,
    navigate,
    isDimensionRequired,
  } = useEditForm(unit, id, catalogSearch, canEditSupplier);

  const saveState = isPending ? 'loading' : 'idle';
  const saveLabel = isPending ? t('form.saveButton.saving') : t('form.saveButton.saveChanges');
  const btnVariant = 'default' as const;
  const btnIcon =
    saveState === 'loading' ? (
      <Loader2 className="h-4 w-4 animate-spin" />
    ) : (
      <Save className="h-4 w-4" />
    );

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void handleSubmit((v) => updateUnit(v), onInvalidSubmit)();
      }}
      className="flex h-full flex-col"
      data-testid="media-edit-form"
    >
      {/* Scrollable, horizontally-centered form content. The footer below is a
          sibling so the sticky bar can span the full viewport width while its
          inner container keeps the buttons aligned to this column's edges. */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-6 max-w-4xl mx-auto space-y-6">
          <div className="flex items-center gap-3">
            <Link
              to="/media-units/$id"
              params={{ id }}
              search={catalogSearch != null ? { catalogSearch } : undefined}
              className="inline-flex items-center gap-1.5 text-[13px] text-muted-foreground hover:text-foreground transition-colors"
              data-testid="back-link"
            >
              <ArrowLeft className="h-4 w-4" />
              {t('form.edit.backLink')}
            </Link>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3">
                <h2 className="text-2xl font-bold tracking-tight" data-testid="form-title">
                  {t('form.edit.title')}
                </h2>
                {isFormDirty ? (
                  <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-50">
                    {t('form.edit.unsavedBadge')}
                  </Badge>
                ) : null}
              </div>
              <p className="text-[13px] text-muted-foreground mt-1" data-testid="form-subtitle">
                {t('form.edit.subtitleText', {
                  displayNumber: unit.displayNumber,
                })}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5" data-testid="form-required-hint">
                {t('form.edit.requiredHint')}
              </p>
            </div>
          </div>
          {showDiscardConfirm ? (
            <DiscardConfirmBanner
              onConfirm={() =>
                void navigate({
                  to: '/media-units/$id',
                  params: { id },
                  search: catalogSearch != null ? { catalogSearch } : undefined,
                })
              }
              onCancel={() => setShowDiscardConfirm(false)}
            />
          ) : null}
          <PhotoUploadSection
            mode="edit"
            existingPhotos={existingPhotos}
            newPhotos={newPhotos}
            onFileChange={handleFileChange}
            onRemoveExistingPhoto={handleRemoveExistingPhoto}
            onRemoveNewPhoto={handleRemoveNewPhoto}
            fileInputRef={fileInputRef}
            photoCountError={photoCountError}
            t={t}
          />
          <SupplierSection
            register={register}
            errors={errors}
            watch={watch}
            setValue={setValue}
            supplierOptions={supplierOptions}
            isSuppliersLoading={false}
            t={t}
            shouldDirty
            canEditSupplier={canEditSupplier}
          />
          <TechnicalSection
            register={register}
            errors={errors}
            watch={watch}
            setValue={setValue}
            formatOptions={formatOptions}
            computedArea={area}
            t={t}
            shouldDirty
            isDimensionRequired={isDimensionRequired}
          />
          <LocationSection
            register={register}
            errors={errors}
            watch={watch}
            setValue={setValue}
            handleRegionChange={handleRegionChange}
            filteredCounties={filteredCounties}
            voivodeshipOptions={voivodeshipOptions}
            t={t}
            shouldDirty
          />
          <CostsSection
            register={register}
            errors={errors}
            t={t}
            subtitle={t('form.sections.costsEditSubtitle')}
            canEditRent={canEditRent}
            canEditCosts={canEditCosts}
          >
            <Separator />
            <CostHistorySection history={costHistory} />
          </CostsSection>
          <NotesSection register={register} errors={errors} t={t} />
          <TagsSection
            tagDefinitionIds={watch('tagDefinitionIds') ?? []}
            onChange={(ids) => setValue('tagDefinitionIds', ids, { shouldDirty: true })}
            t={t}
          />
        </div>
      </div>
      {/* Full-width sticky action bar: background and top border span the viewport,
          while the inner max-w-4xl container keeps the buttons edge-aligned with the
          centered form column above. */}
      <div className="sticky bottom-0 z-10 shrink-0 border-t border-border bg-background shadow-sm">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-6 py-3">
          <Button
            type="button"
            variant="outline"
            data-testid="cancel-button"
            onClick={handleCancel}
          >
            {tCommon('actions.cancel')}
          </Button>
          <Button
            type="button"
            variant={btnVariant}
            disabled={saveState === 'loading'}
            onClick={() => void handleSubmit((v) => updateUnit(v), onInvalidSubmit)()}
            data-testid="save-button-secondary"
          >
            {btnIcon}
            {saveLabel}
          </Button>
        </div>
      </div>
    </form>
  );
}
